import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, Trophy, Crown, Medal, Star, Users, DollarSign, ArrowLeft, Share2 } from 'lucide-react';
import { API } from '../config/api';
import { DonateButton } from '../components/DonateModal';
import { TopSupporters } from '../components/TopSupporters';
import { toast } from 'sonner';

export default function SupportersPage() {
  const navigate = useNavigate();

  const handleShare = () => {
    const url = window.location.href;
    const text = "Support DMV Soundstage - Help build a stronger creative community for DC, Maryland & Virginia artists!";
    
    if (navigator.share) {
      navigator.share({ title: 'DMV Soundstage Supporters', text, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="supporters-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-500" />
                Top Supporters
              </h1>
              <p className="text-muted-foreground">
                The amazing people who help keep DMV Soundstage running
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleShare}>
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {/* Hero CTA */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Support the Platform
              </h2>
              <p className="text-muted-foreground max-w-lg">
                Your donation helps keep the platform running, supports local creators, 
                improves features, and keeps everything ad-light. Every contribution matters!
              </p>
            </div>
            <DonateButton 
              variant="default" 
              size="lg"
              className="bg-gradient-to-r from-primary to-secondary whitespace-nowrap"
            />
          </div>
        </Card>

        {/* Badge Legend */}
        <Card className="p-4 mb-8 glass-card border-border">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Supporter Badges</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-slate-300 to-slate-100 flex items-center justify-center">
                <Crown className="w-4 h-4 text-slate-900" />
              </div>
              <div>
                <p className="font-medium text-sm">Platinum</p>
                <p className="text-xs text-muted-foreground">$100+</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-yellow-900" />
              </div>
              <div>
                <p className="font-medium text-sm">Gold</p>
                <p className="text-xs text-muted-foreground">$50+</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-400 to-gray-300 flex items-center justify-center">
                <Medal className="w-4 h-4 text-gray-800" />
              </div>
              <div>
                <p className="font-medium text-sm">Silver</p>
                <p className="text-xs text-muted-foreground">$25+</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-600 to-orange-400 flex items-center justify-center">
                <Medal className="w-4 h-4 text-orange-100" />
              </div>
              <div>
                <p className="font-medium text-sm">Bronze</p>
                <p className="text-xs text-muted-foreground">$10+</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-rose-400 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-sm">Supporter</p>
                <p className="text-xs text-muted-foreground">Any amount</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Top Supporters Leaderboard */}
        <TopSupporters limit={20} />

        {/* Bottom CTA */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">
            Every donation, big or small, helps build a stronger creative community.
          </p>
          <DonateButton 
            variant="outline" 
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          />
        </div>
      </div>
    </div>
  );
}
